import { useMemo, useState, type FC } from "react";
import { formatDate } from "../lib/date_utils";
import {
  useNotifications,
  toggleNotification,
  deleteNotification,
} from "../stores/notification_store";
import {
  HOOK_TYPES,
  type NotificationTrigger,
  type NotificationHookType,
  type Notification,
} from "../../../shared/models/notification";
import { Pagination } from "../components/pagination";
import { modalManager } from "../lib/modal_store";

const TRIGGER_BADGE: Record<
  NotificationTrigger,
  { cls: string; label: string }
> = {
  issued: { cls: "vmx-badge-success", label: "Issued" },
  expiring: { cls: "vmx-badge-warning", label: "Expiring" },
  expired: { cls: "vmx-badge-danger", label: "Expired" },
  revoked: { cls: "vmx-badge-neutral", label: "Revoked" },
  renewed: { cls: "vmx-badge-accent", label: "Renewed" },
};

function hookLabel(type: NotificationHookType): string {
  return HOOK_TYPES.find((h) => h.value === type)?.label ?? type;
}

export const Notifications: FC = () => {
  const notifications = useNotifications();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const sortedNotifications = useMemo(
    () =>
      [...notifications].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [notifications]
  );

  const totalItems = sortedNotifications.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedNotifications = sortedNotifications.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize
  );

  const openCreate = () => {
    modalManager.open("createNotification", {});
  };

  const openDelete = (n: Notification) => {
    modalManager.open("deleteConfirm", {
      title: "Delete Notification",
      message: (
        <p>
          Are you sure you want to delete <strong>{n.name}</strong>? This action
          cannot be undone.
        </p>
      ),
      onConfirm: () => {
        deleteNotification(n.id);
      },
    });
  };

  return (
    <div className="vmx-page">
      <div className="vmx-page-header">
        <div>
          <div className="vmx-page-title">
            🔔 Notifications (not implemented)
          </div>
          <div className="vmx-page-subtitle">
            Configure alerts for certificate lifecycle events like issuance,
            expiration, and revocation.
          </div>
        </div>
        <div className="vmx-page-actions">
          <button className="vmx-btn vmx-btn-primary" onClick={openCreate}>
            ＋ New Notification
          </button>
        </div>
      </div>

      {sortedNotifications.length === 0 ? (
        <div className="vmx-card">
          <div className="vmx-empty">
            <div className="vmx-empty-icon">🔔</div>
            <div className="vmx-empty-title">No notifications configured</div>
            <div className="vmx-empty-text">
              Create a notification to get alerted via webhooks, SNS, Slack, and
              more when certificate events occur.
            </div>
            <button className="vmx-btn vmx-btn-primary" onClick={openCreate}>
              ＋ Create Notification
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="vmx-table-wrapper vmx-card-table-wrapper">
            <table className="vmx-table vmx-card-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Trigger</th>
                  <th>Hooks</th>
                  <th>Status</th>
                  <th>Created ▾</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedNotifications.map((n) => {
                  const trigger = TRIGGER_BADGE[n.trigger];
                  return (
                    <tr key={n.id}>
                      <td
                        data-label="Name"
                        style={{
                          fontWeight: 600,
                          color: "var(--vmx-gray-800)",
                        }}
                      >
                        {n.name}
                      </td>
                      <td data-label="Trigger">
                        <span className={`vmx-badge ${trigger.cls}`}>
                          {trigger.label}
                        </span>
                      </td>
                      <td data-label="Hooks">
                        <div
                          style={{ display: "flex", gap: 4, flexWrap: "wrap" }}
                        >
                          {n.hooks.map((h, i) => (
                            <span
                              key={i}
                              className="vmx-badge vmx-badge-neutral"
                              style={{
                                fontFamily: "var(--vmx-font-mono)",
                                fontSize: "0.75rem",
                                opacity: h.enabled ? 1 : 0.5,
                              }}
                            >
                              {hookLabel(h.type)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td data-label="Status">
                        <span
                          className={`vmx-badge ${n.enabled ? "vmx-badge-success" : "vmx-badge-neutral"}`}
                        >
                          {n.enabled ? "Active" : "Disabled"}
                        </span>
                      </td>
                      <td
                        data-label="Created"
                        style={{
                          color: "var(--vmx-gray-400)",
                          fontSize: "0.8125rem",
                        }}
                      >
                        {formatDate(n.createdAt)}
                      </td>
                      <td data-label="Actions">
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            className="vmx-btn vmx-btn-ghost vmx-btn-sm"
                            onClick={() => toggleNotification(n.id)}
                          >
                            {n.enabled ? "Disable" : "Enable"}
                          </button>
                          <button
                            className="vmx-btn vmx-btn-ghost vmx-btn-sm vmx-delete-notification-btn"
                            style={{ color: "var(--vmx-danger)" }}
                            onClick={() => openDelete(n)}
                          >
                            <span className="vmx-delete-icon">✕</span>
                            <span className="vmx-delete-label">Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={safePage}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
          />
        </>
      )}
    </div>
  );
};
